// Card-schema versioning & migration.
//
// Cards carry a `schema_version` field so we can evolve the schema (rename
// effect keys, remove fields, reshape values) without breaking stored data.
// In-repo preset cards at data/cards/*.json are easy to update directly, but
// generated custom cards live under data/custom-cards/ on the VPS and are
// excluded from rsync — those can be arbitrarily old.
//
// How to change the schema:
//   1. Bump CURRENT_SCHEMA_VERSION.
//   2. Add `migrate_N_to_N+1(card)` with a doc comment describing the change.
//   3. Append it to the MIGRATIONS array — never delete or reorder existing
//      entries; old data still depends on the full chain.
//
// See CLAUDE.md "Card Schema Versioning & Migration" for the full workflow.

import type { CardDef } from '../types.js';
import type { Target } from './targeting.js';

export const CURRENT_SCHEMA_VERSION = 5;

type Migration = (card: any) => any;

// 0 → 1: Introduces the `schema_version` field. No semantic change — every
// card written before this system existed is implicitly v0 and just gets the
// field stamped on.
const migrate_0_to_1: Migration = (card) => ({ ...card, schema_version: 1 });

// 1 → 2: Splits decomposable targeting slugs (damage-weakest-enemy, boost-allies,
// armor-all, etc.) into (verb, target) pairs. Adds a `target` field to the
// affected ability or action effect; `effect` is rewritten to the verb.
// `ready-all` is special-cased: on a character's *activated* ability it maps
// to 'all-other-allies' (preserves the anti-infinite-combo behavior), while
// on-play and action-card uses map to 'all-allies'. Untouched slugs
// (damage-direct/damage-opponent, heal, draw, compound effects, passives) stay as-is.
const SPLIT_MAP: Record<string, { effect: string; target: Target }> = {
  'damage-weakest-enemy':    { effect: 'damage',  target: 'weakest-enemy' },
  'damage-strongest-enemy':  { effect: 'damage',  target: 'strongest-enemy' },
  'damage-all-enemies':      { effect: 'damage',  target: 'all-enemies' },
  'boost-self':              { effect: 'boost',   target: 'self' },
  'boost-weakest-ally':      { effect: 'boost',   target: 'weakest-ally' },
  'boost-strongest-ally':    { effect: 'boost',   target: 'strongest-ally' },
  'boost-allies':            { effect: 'boost',   target: 'all-other-allies' },
  'armor-self':              { effect: 'armor',   target: 'self' },
  'armor-weakest-ally':      { effect: 'armor',   target: 'weakest-ally' },
  'armor-strongest-ally':    { effect: 'armor',   target: 'strongest-ally' },
  'armor-all':               { effect: 'armor',   target: 'all-allies' },
  'exhaust-weakest-enemy':   { effect: 'exhaust', target: 'weakest-enemy' },
  'exhaust-strongest-enemy': { effect: 'exhaust', target: 'strongest-enemy' },
  'return-weakest-ally':     { effect: 'return',  target: 'weakest-ally' },
  'return-weakest-enemy':    { effect: 'return',  target: 'weakest-enemy' },
  'return-strongest-enemy':  { effect: 'return',  target: 'strongest-enemy' },
  'slay-giant':              { effect: 'slay',    target: 'strongest-enemy' },
};

function splitForReadyAll(abilityType?: string): { effect: string; target: Target } {
  return {
    effect: 'ready',
    target: abilityType === 'activated' ? 'all-other-allies' : 'all-allies',
  };
}

const migrate_1_to_2: Migration = (card) => {
  const next: any = { ...card, schema_version: 2 };

  // Action card: effect sits at the root.
  if (next.type === 'action' && typeof next.effect === 'string') {
    const eff = next.effect;
    const split = eff === 'ready-all' ? splitForReadyAll(undefined) : SPLIT_MAP[eff];
    if (split) { next.effect = split.effect; next.target = split.target; }
  }

  // Character card: effect sits on ability.
  if (next.type === 'character' && next.ability && typeof next.ability.effect === 'string') {
    const eff = next.ability.effect;
    const split = eff === 'ready-all' ? splitForReadyAll(next.ability.type) : SPLIT_MAP[eff];
    if (split) {
      next.ability = { ...next.ability, effect: split.effect, target: split.target };
    }
  }

  return next;
};

// 2 → 3: Atomic-slug clarity renames. The effects still do exactly the same
// thing, but the names are easier to reason about:
//   damage-direct          → damage-opponent
//   double-exhaust         → keep-exhausted       (the old name implied
//                                                  "exhaust twice", but the
//                                                  effect keeps an already-
//                                                  exhausted enemy exhausted
//                                                  through their next turn)
//   passive-damage-frozen  → passive-damage-exhausted ("frozen" was jargon;
//                                                  "exhausted" is the actual
//                                                  game-state name)
const RENAME_2_TO_3: Record<string, string> = {
  'damage-direct':         'damage-opponent',
  'double-exhaust':        'keep-exhausted',
  'passive-damage-frozen': 'passive-damage-exhausted',
};

const migrate_2_to_3: Migration = (card) => {
  const next: any = { ...card, schema_version: 3 };
  if (next.type === 'action' && typeof next.effect === 'string' && RENAME_2_TO_3[next.effect]) {
    next.effect = RENAME_2_TO_3[next.effect];
  }
  if (next.type === 'character' && next.ability && typeof next.ability.effect === 'string' && RENAME_2_TO_3[next.ability.effect]) {
    next.ability = { ...next.ability, effect: RENAME_2_TO_3[next.ability.effect] };
  }
  return next;
};

// 3 → 4: Introduces top-level `description` (a one-sentence card lore from
// cardgen's candidate phase, or the flavor text as a fallback for old cards)
// and drops the unused effect-text override fields (`Ability.description` and
// `ActionCardDef.description`). Those fields were a latent "manual override"
// slot that nothing ever populated; effect text is always computed from
// effect + target + intensity now. If any legacy data did have them set,
// we strip the override and let the computed text win — simpler and correct
// for the current catalog (0 of 129 preset cards used the slot).
const migrate_3_to_4: Migration = (card) => {
  const next: any = { ...card, schema_version: 4 };
  if (next.ability && 'description' in next.ability) {
    const { description, ...rest } = next.ability;
    next.ability = rest;
  }
  if (next.type === 'action' && 'description' in next) {
    // Action cards' old `description` was an effect-text override. Strip it
    // before we re-purpose the field at the top level as card lore.
    delete next.description;
  }
  // Populate top-level description from flavor as a lore fallback. Cards
  // generated by the v4+ pipeline will set description directly.
  if (typeof next.description !== 'string' || next.description.length === 0) {
    if (typeof next.flavor === 'string' && next.flavor.length > 0) {
      next.description = next.flavor;
    }
  }
  return next;
};

// 4 → 5: `keep-exhausted` became a verb instead of an atomic effect, so
// it now requires an explicit target. Old data that has `effect:
// "keep-exhausted"` with no target gets stamped with `target:
// "strongest-enemy"` — that was the hardcoded behavior in the previous
// atomic implementation, so existing cards keep their original meaning.
const migrate_4_to_5: Migration = (card) => {
  const next: any = { ...card, schema_version: 5 };
  if (next.type === 'character' && next.ability?.effect === 'keep-exhausted' && !next.ability.target) {
    next.ability = { ...next.ability, target: 'strongest-enemy' };
  }
  if (next.type === 'action' && next.effect === 'keep-exhausted' && !next.target) {
    next.target = 'strongest-enemy';
  }
  return next;
};

// Ordered by source version: MIGRATIONS[N] migrates N → N+1. Never delete or
// reorder entries; old data out in the wild still depends on the full chain.
const MIGRATIONS: Migration[] = [
  migrate_0_to_1,
  migrate_1_to_2,
  migrate_2_to_3,
  migrate_3_to_4,
  migrate_4_to_5,
];

export function migrateCard(raw: unknown): CardDef {
  const card = raw as any;
  let version = typeof card?.schema_version === 'number' ? card.schema_version : 0;
  let current = card;
  while (version < CURRENT_SCHEMA_VERSION) {
    const step = MIGRATIONS[version];
    if (!step) {
      throw new Error(`No migration registered for version ${version} → ${version + 1}`);
    }
    current = step(current);
    version++;
  }
  return current as CardDef;
}
